import { USER_CLS } from "../../../utils/constants";
import { ApplyFunction } from "../ApplyFunction";
import { Link, LinkUtils } from "../Link";
import { Param, ParamUtils } from "../Param";
import { Process, ProcessUtils } from "../Process";
import { DetailedSafeObject, DetailedSafeObjectUtils } from "./IDetailedSafeObjecet";

export interface DetailedUser extends DetailedSafeObject {
  readonly link_Path: string;
  readonly public_key: string;
  readonly public_keys: string[];
  readonly dependencies: string;
  readonly link: Link;
  readonly profile: Param[];
  readonly name: string;
}

export const DetailedUserUtils = {
  create({
    id,
    isValid,
    process,
    nestedObjects,
    isDeleted,
    applyFunction,
    originalObjectSIG,
    source,
    destination,
    timestamp,
    last_modified,
    slot,
    pending_objects,
    affected_by,
    parentGroup,
    SIGNATURE = "",
    comment = "",
    safes,
    trustors,
    public_key,
    execute,
    link_Path,
    public_keys,
    dependencies,
    link,
    profile,
  }: {
    id: string;
    isValid: boolean;
    process: Process;
    nestedObjects: DetailedSafeObject[];
    isDeleted: boolean;
    applyFunction: ApplyFunction | null;
    originalObjectSIG: string;
    source: string;
    destination: string;
    timestamp: number;
    last_modified: number;
    slot: number;
    pending_objects: DetailedSafeObject[];
    affected_by: DetailedSafeObject[];
    parentGroup: string;
    SIGNATURE?: string;
    comment?: string;
    safes?: Array<Record<string, any>> | null;
    trustors?: Array<Record<string, any>> | null;
    public_key?: string | null;
    execute: boolean;
    link: Link;
    profile: Param[];
    dependencies: string;
    link_Path: string;
    public_keys: string[];
  }): DetailedUser {
    return {
      cls: USER_CLS,
      id,
      parentGroup,
      SIGNATURE,
      originalObjectSIG,
      comment,
      applyFunction,
      execute,
      isValid,
      process,
      nestedObjects,
      isDeleted,
      source,
      destination,
      timestamp,
      last_modified,
      slot,
      pending_objects,
      affected_by,
      safes: safes ?? null,
      trustors: trustors ?? null,
      public_key: public_key ?? null,
      link_Path,
      public_keys: public_keys ?? [],
      dependencies: dependencies ?? "",
      link: link ?? LinkUtils.empty(),
      profile: profile ?? [],
      name: (() => {
        const param = profile.find((p) => p.key === "user_name");
        if (param) {
          return param.value;
        } else {
          return "Not Available";
        }
      })(),
    };
  },

  empty(): DetailedUser {
    const tms = Date.now();
    return DetailedUserUtils.create({
      id: "",
      isValid: false,
      process: ProcessUtils.empty(),
      nestedObjects: [],
      isDeleted: false,
      applyFunction: null,
      originalObjectSIG: "",
      source: "",
      destination: "",
      timestamp: tms,
      last_modified: tms,
      slot: -1,
      pending_objects: [],
      affected_by: [],
      parentGroup: "",
      SIGNATURE: "",
      comment: "",
      safes: null,
      trustors: null,
      public_key: null,
      execute: true,
      link_Path: "",
      public_keys: [],
      dependencies: "",
      link: LinkUtils.empty(),
      profile: [],
    });
  },

  toMap(user: DetailedUser): Record<string, any> {
    const json = DetailedSafeObjectUtils.toMap(user);
    json["object"] = {
      ...json["object"],
      link_Path: user.link_Path,
      public_key: user.public_key,
      dependencies: user.dependencies,
      profile: user.profile.map((p) => ParamUtils.toMap(p)),
    };
    json["link"] = LinkUtils.toMap(user.link);
    return json;
  },

  copyWith({
    profile,
    link_Path,
    public_key,
    public_keys,
    dependencies,
    id,
    parentGroup,
    name,
    SIGNATURE,
    comment,
    process,
    nestedObjects,
    isValid,
    isDeleted,
    applyFunction,
    originalObjectSIG,
    source,
    destination,
    slot,
    timestamp,
    last_modified,
    link,
    pending_objects,
    affected_by,
    execute,
  }: {
    profile?: Param[];
    link_Path?: string;
    public_key?: string;
    public_keys?: string[];
    dependencies?: string;
    id?: string;
    parentGroup?: string;
    name?: string;
    SIGNATURE?: string;
    comment?: string;
    process?: Process;
    nestedObjects?: DetailedSafeObject[];
    isValid?: boolean;
    isDeleted?: boolean;
    applyFunction?: ApplyFunction | null;
    originalObjectSIG?: string;
    source?: string;
    destination?: string;
    slot?: number;
    timestamp?: number;
    last_modified?: number;
    link?: Link;
    pending_objects?: DetailedSafeObject[];
    affected_by?: DetailedSafeObject[];
    execute?: boolean;
  }): DetailedUser {
    return DetailedUserUtils.create({
      isDeleted: isDeleted ?? false,
      isValid: isValid ?? false,
      applyFunction: applyFunction ?? null,
      originalObjectSIG: originalObjectSIG ?? "",
      link_Path: link_Path ?? "",
      public_key: public_key ?? "",
      public_keys: public_keys ?? [],
      dependencies: dependencies ?? "",
      link: link ?? LinkUtils.empty(),
      profile: profile ?? [],
      id: id ?? "",
      parentGroup: parentGroup ?? "",
      SIGNATURE: SIGNATURE ?? "",
      comment: comment ?? "",
      process: process ?? ProcessUtils.empty(),
      nestedObjects: nestedObjects ?? [],
      source: source ?? "",
      destination: destination ?? "",
      slot: slot ?? -1,
      timestamp: timestamp ?? Date.now(),
      last_modified: last_modified ?? Date.now(),
      pending_objects: pending_objects ?? [],
      affected_by: affected_by ?? [],
      execute: execute ?? true,
    });
  },
};
